<script>
import { useVuelidate } from '@vuelidate/core';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ButtonNext from 'next/button/Button.vue';
import Draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import getUuid from 'widget/helpers/uuid';
import { setStepMoveInProgress } from 'dashboard/store/utils/wgptAutomationBuilderUtils';
import AddStepButton from '../../addStepDropdown/AddStepButton.vue';
import Dropdown from '../../Dropdown.vue';
import actionValidationMixins from './helpers/actionValidationMixins';

export default {
  components: {
    AddStepButton,
    Dropdown,
    Draggable,
    ButtonNext,
  },
  mixins: [actionValidationMixins],
  props: {
    step: {
      type: Object,
      default: () => {},
    },
    first: {
      type: Boolean,
      default: true,
    },
    last: {
      type: Boolean,
      default: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    variables: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['addStepBefore', 'stepDelete', 'addStepAfter', 'stepChange'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      isDragging: false,
      willAddStepAfter: null,
    };
  },
  computed: {
    ...mapGetters({
      actionClipboard: 'wgptAutomations/getActionClipboard',
      getLinkedSteps: 'wgptAutomationBuilder/getLinkedSteps',
    }),
    actionRegistry() {
      return this.$store.getters['wgptAutomationRegistry/getAction'](
        this.step.action
      );
    },
    draggableStep: {
      get() {
        return [this.step];
      },
      set(steps) {
        if (steps.length === 0) {
          this.onStepMove();
        } else if (steps.length > 1) {
          this.onStepDropped(steps);
        }
      },
    },
    isBeingCut() {
      const clipboard = this.actionClipboard;
      if (!clipboard) return false;

      const { action, operation } = clipboard;
      if (operation !== 'cut') return false;

      return action.id === this.step.id;
    },
    stepRoute() {
      if (this.v$.step.$error) {
        return { query: { selected: this.step.id, dirty: true } };
      }

      return { query: { selected: this.step.id } };
    },
    linkedStepRoute() {
      if (this.step.action !== 'jump_to_step') return null;

      const targetStepId = this.step.attributes?.step_id;
      if (!targetStepId) return null;

      return { query: { selected: targetStepId } };
    },
    linkedSteps() {
      return this.getLinkedSteps(this.step.id);
    },
    isActive() {
      return this.$route.query.selected === this.step.id;
    },
    isLinked() {
      const selected = this.$route.query.selected;
      return this.linkedSteps.some(step => step.id === selected);
    },
  },
  methods: {
    deleteStep() {
      this.$emit('stepDelete', this.step.id);
    },
    updateStep(stepUpdates) {
      this.$emit('stepChange', this.step.id, stepUpdates);
    },
    onStepMove() {
      setStepMoveInProgress(true);
      this.deleteStep();
      setStepMoveInProgress(false);
    },
    onStepDropped(steps) {
      const droppedStepsBefore = [];
      const droppedStepsAfter = [];
      const currentStepId = this.step.id;
      let found = false;

      steps.forEach(step => {
        if (step.id === currentStepId) {
          found = true;
          return;
        }

        if (found) {
          droppedStepsAfter.push(step);
        } else {
          droppedStepsBefore.push(step);
        }
      });

      const selectedStepId = this.$route.query.selected;
      let newSelectedId = null;

      droppedStepsBefore.forEach(step => {
        const newId = getUuid();
        if (step.id === selectedStepId) newSelectedId = newId;
        this.$emit('addStepBefore', { ...step, id: newId });
      });

      droppedStepsAfter.forEach(step => {
        const newId = getUuid();
        if (step.id === selectedStepId) newSelectedId = newId;
        this.$emit('addStepAfter', { ...step, id: newId });
      });

      if (newSelectedId) {
        this.$router
          .replace({ query: { selected: newSelectedId } })
          .catch(() => {});
      }
    },
    cutStep() {
      this.$store.dispatch('wgptAutomations/cutAction', this.step);
    },
    copyStep() {
      this.$store.dispatch('wgptAutomations/copyAction', this.step);
    },
    jumpToStep() {
      if (this.linkedStepRoute) {
        this.$router.push(this.linkedStepRoute);
      }
    },
  },
};
</script>

<template>
  <div class="action">
    <div v-if="first" class="connector-top">
      <AddStepButton
        class="my-4"
        :class="{ invisible: disabled && false }"
        @add-step="e => $emit('addStepBefore', e)"
        @delete-step="e => $emit('stepDelete', e)"
      />
    </div>
    <Dropdown :disabled="disabled" class="mx-auto w-max">
      <Draggable
        v-model="draggableStep"
        item-key="id"
        group="steps"
        class="draggable step-draggable"
        :class="{
          'step-error': v$.step.$error,
          'step-active [&_.linked-button]:visible': isActive,
          'step-cut-active': isBeingCut,
          'step-linked': isLinked,
        }"
      >
        <template #item="{ element: stepEl }">
          <router-link :to="stepRoute" replace class="block">
            <div :id="stepEl.id" class="action-content">
              <div class="py-2 px-3 border-b border-n-strong font-semibold">
                <span class="text-base font-medium text-n-slate-12">
                  {{
                    stepEl.name ||
                    $t('WGPT_AUTOMATIONS.EDITOR.ACTION.FALLBACK_ACTION_NAME')
                  }}
                </span>
              </div>
              <div class="py-2 px-3 flex items-center justify-between">
                <p class="text-xs text-n-slate-11 m-0">
                  <span v-if="actionRegistry">
                    {{ toLocale(actionRegistry.name) }}
                  </span>
                  <span v-else class="space-y-2 animate-loader-pulse">
                    <div
                      class="bg-slate-100 dark:bg-slate-800/50 rounded w-1/2"
                    >
                      &nbsp;
                    </div>
                  </span>
                </p>
                <ButtonNext
                  v-if="linkedStepRoute"
                  variant="ghost"
                  size="xs"
                  icon="i-lucide-redo-dot"
                  class="linked-button invisible [&_span]:size-4 absolute end-0.5 top-0.5"
                  @click.prevent="jumpToStep"
                />
              </div>
            </div>
          </router-link>
        </template>
      </Draggable>

      <template #popper>
        <div class="flex gap-1">
          <div class="bg-n-solid-2 rounded-md shadow-sm border border-n-weak">
            <ButtonNext
              color="slate"
              variant="ghost"
              size="xs"
              icon="i-lucide-copy"
              @click="copyStep"
            />
            <ButtonNext
              color="slate"
              variant="ghost"
              size="xs"
              icon="i-lucide-scissors"
              @click="cutStep"
            />
            <ButtonNext
              color="ruby"
              variant="ghost"
              size="xs"
              icon="i-lucide-trash"
              @click="deleteStep"
            />
          </div>
        </div>
      </template>
    </Dropdown>

    <div class="connector-bottom">
      <AddStepButton
        class="my-4"
        :class="{ 'mb-0': last, invisible: disabled, hidden: disabled && last }"
        @add-step="e => $emit('addStepAfter', e)"
        @delete-step="e => $emit('stepDelete', e)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.step-draggable {
  @apply relative bg-slate-100 dark:bg-slate-800/50 min-w-[200px] min-h-[75px] mx-2 rounded-xl;
}

.action-content {
  @apply rounded-xl shadow-sm border border-n-strong bg-n-solid-2 dark:bg-n-solid-3;
  @apply w-full relative cursor-pointer;
}

.step-active:not(.error) {
  .action-content {
    @apply outline-none;
    outline-color: var(--actionThemeColor);
    outline-offset: 1px;
  }
}

.step-cut-active {
  .action-content {
    @apply opacity-50;
  }
}

.step-error {
  .action-content {
    @apply outline-none outline-n-ruby-8;
    outline-offset: 1px;
  }
}

.step-linked:not(.step-error, .step-active) {
  .action-content {
    @apply outline-none;
    outline-color: var(--linkedThemeColor);
    outline-offset: 1px;
    outline-style: dashed;
  }
}

[dir='ltr'] {
  .connector-top,
  .connector-bottom {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-r-[1px] border-dashed border-slate-400 dark:border-slate-600;
    --transform: translateX(50%);
  }
}
[dir='rtl'] {
  .connector-top,
  .connector-bottom {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-l-[1px] border-dashed border-slate-400 dark:border-slate-600;
    --transform: translateX(-50%);
  }
}

.sortable-ghost {
  @apply border-none h-[2px] w-full absolute;
  &::before {
    @apply block !bg-n-brand rounded-full absolute w-full;
    content: '';
    height: 4px;
  }
  &:first-child::before {
    @apply -mt-4;
  }
  &:last-child::before {
    @apply mt-3;
  }
  .action-content {
    @apply opacity-0;
  }
}

.sortable-chosen .action-content {
  outline-color: transparent !important;
}

.step-draggable .sortable-ghost:first-child:last-child::before {
  @apply hidden;
}

.branch-draggable .sortable-ghost:before {
  @apply -mt-7 mx-2;
}

.condition-draggable .sortable-ghost:last-child::before {
  @apply hidden;
}

.linked-button {
  color: var(--linkedThemeColor);
}
</style>
